import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar from '../../components/FilterBar';
import { nameOnlyFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal, Button } from '../../components/ui';
import { SkeletonTable } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/PageHeader';
import Modal from '../../components/Modal';
import SchemaForm from '../../schemaForm/SchemaForm';
import { translateError } from '../../utils/errorMessages';

interface ISection {
  _id: string | number;
  name: string;
  description?: string;
  sortOrder?: number;
  activityIds: number[];
}

const SectionsListPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [sections, setSections] = useState<ISection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filters, setFilters] = useState<FilterValue[]>([]);

  // Create/edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<ISection | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);

  const canCreate = hasPermission('activities:write');
  const canEdit = hasPermission('activities:write');
  const canDelete = hasPermission('activities:write');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const sectionsRes = await api.sections.list();
      setSections(sectionsRes.data as ISection[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateModal = () => {
    if (!canCreate) return;
    setEditingSection(null);
    setModalOpen(true);
  };

  const openEditModal = (section: ISection) => {
    if (!canEdit) return;
    setEditingSection(section);
    setModalOpen(true);
  };

  // SchemaForm seeds activityIds from the section row (number[]) for editing.
  const initialValues = useMemo(
    () =>
      editingSection
        ? {
            name: editingSection.name,
            description: editingSection.description ?? '',
            activityIds: (editingSection.activityIds || []).map(Number),
          }
        : undefined,
    [editingSection]
  );

  // SchemaForm m2m emits number[]; the backend dedups + Numbers, so we pass
  // values straight through.
  const handleSave = async (values: Record<string, any>) => {
    setSaveLoading(true);
    try {
      const payload = {
        name: String(values.name ?? '').trim(),
        description: String(values.description ?? '').trim() || undefined,
        activityIds: values.activityIds ?? [],
      };
      if (editingSection) {
        await api.sections.update(Number(editingSection._id), payload);
        addToast(t('sections.updated'), 'success');
      } else {
        await api.sections.create(payload);
        addToast(t('sections.created'), 'success');
      }
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteClick = (section: ISection) => {
    if (!canDelete) return;
    setDeleteId(section._id);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId === null) return;
    setDeleteLoading(true);
    try {
      await api.sections.delete(Number(deleteId));
      addToast(t('sections.deleted'), 'success');
      fetchData();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const truncate = (text: string, max = 60) =>
    text.length > max ? `${text.slice(0, max)}…` : text;

  const columns: Column<ISection>[] = useMemo(() => [
    {
      header: t('sections.name'),
      key: 'name',
      sortValue: (section) => section.name ?? null,
    },
    {
      header: t('sections.description'),
      render: (section) => (section.description ? truncate(section.description) : '-'),
      sortValue: (section) => section.description ?? null,
    },
    {
      header: t('sections.activities'),
      render: (section) => String(section.activityIds?.length ?? 0),
      sortValue: (section) => section.activityIds?.length ?? 0,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const filteredSections = useMemo(() => applyFilters(sections, filters), [sections, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  const createAction = canCreate ? (
    <Button variant="primary" size="md" onClick={openCreateModal}>
      {t('sections.create')}
    </Button>
  ) : undefined;

  return (
    <div className="admin-list-page">
      <PageHeader title={t('sections.title')} action={createAction} />

      {loading ? (
        <SkeletonTable rows={5} columns={3} />
      ) : sections.length === 0 ? (
        <EmptyState
          title={t('sections.noSections')}
          description={t('sections.emptyDescription')}
          action={canCreate ? { label: t('sections.create'), onClick: openCreateModal } : undefined}
        />
      ) : (
        <>
          <FilterBar fields={nameOnlyFields} onFilterChange={handleFilterChange} />
          <DataTable
            data={filteredSections}
            columns={columns}
            onEdit={canEdit ? openEditModal : undefined}
            onDelete={canDelete ? handleDeleteClick : undefined}
          />
        </>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingSection ? t('sections.edit') : t('sections.create')}
      >
        <SchemaForm
          entity="section"
          sections={[{ items: ['name', 'description', 'activityIds'] }]}
          initialValues={initialValues}
          onSubmit={handleSave}
          loading={saveLoading}
        />
      </Modal>

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('sections.deleteTitle')}
        message={t('sections.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default SectionsListPage;
